/**
 * The one classifier for media that rides the `media` content block (spec §4).
 * Images stay ImageBlocks; anything else that is not video/audio/PDF stays a
 * plain file pointer. Both the sandbox user-turn adapter and the `watch` tool
 * call this — never a second `startsWith('video/')` anywhere.
 *
 * Named `AttachmentMediaKind` because `MediaKind` is already taken on the
 * package surface by the channel contract (`'file' | 'voice'`, types/channel.ts).
 */
export type AttachmentMediaKind = 'video' | 'audio' | 'document';

export function mediaKindFor(mimeType: string | null | undefined): AttachmentMediaKind | null {
  if (!mimeType) return null;
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'document';
  return null;
}

/**
 * Extension → mime for the media the `media` block carries (video, audio, PDF).
 *
 * THE SECOND OPINION, IN ONE PLACE. A browser's `File.type` is a guess and is
 * routinely EMPTY for a container it cannot name, so three separate doors need
 * to answer "is this media" from the filename alone: the web picker (does the
 * 500 MB ceiling apply at pick time), the server's attachment sync (does this go
 * by reference or by bytes), and the box (does a file the server wrote as bytes
 * mint a media block). Those three tables used to be written out separately and
 * disagreed — an `.mkv` the picker warned about took the byte path on the server
 * and died at the 50 MiB wall after a 500 MB upload. One table, three readers.
 *
 * Deliberately narrow: anything not here is NOT media and stays a plain file
 * pointer. A container Google cannot decode is a placeholder, not a crash
 * (spec §11), so listing a format the model may refuse is the safe side.
 */
export const MEDIA_MIME_BY_EXT: Record<string, string> = {
  mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
  mkv: 'video/x-matroska', mpeg: 'video/mpeg', mpg: 'video/mpeg', avi: 'video/x-msvideo',
  wmv: 'video/x-ms-wmv', flv: 'video/x-flv', '3gp': 'video/3gpp', ogv: 'video/ogg',
  mp3: 'audio/mpeg', m4a: 'audio/mp4', wav: 'audio/wav', aac: 'audio/aac',
  ogg: 'audio/ogg', oga: 'audio/ogg', flac: 'audio/flac', opus: 'audio/opus',
  aiff: 'audio/aiff', amr: 'audio/amr',
  pdf: 'application/pdf',
};

/** The mime this filename's extension implies, or '' when it names no media. */
export function mediaMimeFromFilename(name: string): string {
  return MEDIA_MIME_BY_EXT[(name.split('.').pop() ?? '').toLowerCase()] ?? '';
}
